import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { CiMail } from "react-icons/ci";
import {
  CustomInput,
  CustomSubmitButton,
  CustomAlert,
  CustomModalHeader,
  CustomLinkButton,
  CustomFormStyles
} from './CustomFormComponents';
import { forgotPassword } from '../../../store/auth'; // ✅ Import the API function

const ForgotPassword = ({ show, handleClose, switchToLogin, onForgotPasswordSuccess }) => {
  const [formData, setFormData] = useState({
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({}); // Clear any previous errors
    
    try {
      // ✅ Real API call to backend
      const response = await forgotPassword(formData.email);
      
      // Success - show success screen
      setIsSuccess(true);
      
      // Don't navigate away - user needs to see success message
      // Removed callback to prevent parent component from navigating
      
    } catch (error) {
      // Handle different error types
      console.error('Forgot password error:', error);
      
      let errorMessage = 'Failed to send reset email. Please try again.';
      
      // Check if error has a specific message
      if (error.message) {
        if (error.message.includes('not found') || error.message.includes('does not exist')) {
          errorMessage = 'No account found with this email address.';
        } else if (error.message.includes('Network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setErrors({ general: errorMessage });
      
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setFormData({ email: '' });
    setErrors({});
    setIsSuccess(false);
    handleClose();
  };

  const handleBackToLogin = () => {
    setFormData({ email: '' });
    setErrors({});
    setIsSuccess(false);
    switchToLogin();
  };

  return (
    <>
      <Modal show={show} onHide={handleModalClose} centered backdrop="static" keyboard={false}>
        <CustomModalHeader 
          title="Forgot Password" 
          onClose={handleModalClose}
          showBackButton={true}
          onBack={handleBackToLogin}
        />
        
        <Modal.Body className="px-4 pb-4">
          {!isSuccess ? (
            <>
              <CustomAlert 
                type="danger" 
                message={errors.general} 
                show={!!errors.general} 
              />
              
              <div className="text-center mb-4">
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                  <CiMail size={32} className="text-primary" />
                </div>
                <p style={{ color: '#666' }}>
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>
              
              <form onSubmit={handleSubmit}>
                <CustomInput
                  type="email"
                  name="email"
                  label="Email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  error={errors.email}
                  autoFocus={true}
                  disabled={isLoading}
                />

                <CustomSubmitButton
                  isLoading={isLoading}
                  loadingText="Sending Email..."
                >
                  Send Reset Link
                </CustomSubmitButton>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                <CiMail size={32} className="text-success" />
              </div>
              <h4 className="text-success mb-3">Email Sent!</h4>
              <p style={{ color: '#666' }} className="mb-4">
                We've sent a password reset link to <strong style={{ color: '#000' }}>{formData.email}</strong>. 
                Please check your email and follow the instructions to reset your password.
              </p>
              <div className="alert alert-info" role="alert">
                <small>
                  <strong>Didn't receive the email?</strong><br />
                  Check your spam folder or try again in a few minutes.
                </small>
              </div>
              <Button
                variant="outline-primary"
                onClick={handleBackToLogin}
                className="mt-2"
              >
                Back to Login
              </Button>
            </div>
          )}

          {!isSuccess && (
            <div className="text-center mt-3">
              <p className="mb-0" style={{ color: '#000' }}>
                Remember your password?{' '}
                <CustomLinkButton onClick={handleBackToLogin} disabled={isLoading}
                className="inline align-baseline">
                  Back to Sign in
                </CustomLinkButton>
              </p>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <CustomFormStyles />
    </>
  );
};

export default ForgotPassword;